<template>
    <Dropdown
        placement="bottom-start"
        transfer
        trigger="custom"
        :visible="dropdownIsVisible"
        @onClickoutside="toggleDropdown"
    >
        <hox-dropdown-button
            :is-active="dropdownIsVisible"
            :is-disabled="deliverables.length === 0 || isDisabled"
            :is-loading="isSettingDeliverableStatus"
            data-testid="library__production-status-select"
            @click="toggleDropdown"
        >
            Production status
        </hox-dropdown-button>

        <template #list>
            <qa-status-dropdown-items @statusSelected="setDeliverableStatus" />
        </template>
    </Dropdown>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import type { ConstructedDeliverable } from "@/types/types";

import QaStatusDropdownItems from "@/components/Campaign/QaStatusDropdownItems.vue";
import HoxDropdownButton from "@/components/common/DropdownButton.vue";
import { BannerQaStatusLabel } from "@/enums/filters";
import { setDeliverablesGlobalStatus } from "@/services/Qa";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

export default defineComponent({
    components: {
        HoxDropdownButton,
        QaStatusDropdownItems
    },
    props: {
        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        isDisabled: {
            type: Boolean
        },
        itemToChangeNumber: {
            required: false,
            type: Number
        }
    },

    emits: ["statusHasBeenSet"],

    setup() {
        const ga4 = useGA4Tracking();
        return { ga4 };
    },

    data() {
        return {
            dropdownIsVisible: false,
            isSettingDeliverableStatus: false
        };
    },
    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        campaignId() {
            return this.$route.params.campaignId as string;
        }
    },
    methods: {
        toggleDropdown() {
            this.dropdownIsVisible = !this.dropdownIsVisible;
        },
        async setDeliverableStatus(status: keyof typeof BannerQaStatusLabel) {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.DROPDOWN,
                interactionText: `${GA4.CLICK_INTERACTION_TEXTS.SET_STATUS} - ${BannerQaStatusLabel[status]}`
            });

            this.toggleDropdown();
            this.isSettingDeliverableStatus = true;
            const deliverablesCount = this.itemToChangeNumber || this.deliverables.length;
            try {
                const deliverables = await setDeliverablesGlobalStatus(
                    this.campaignId,
                    this.deliverables,
                    status,
                    this.creativeLibraryFiltersStore.getComputedLibraryFilters
                );
                this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, deliverables);
                if (deliverablesCount === 1) {
                    this.$snackbar.success(`The deliverable is now "${BannerQaStatusLabel[status]}"`);
                } else {
                    this.$snackbar.success(
                        `${deliverablesCount} deliverables are now "${BannerQaStatusLabel[status]}"`
                    );
                }
                this.$emit("statusHasBeenSet");
            } catch {
                this.$snackbar.error(
                    "There was an unexpected error and the deliverable status was unable to be set. Please try again in a few moments."
                );
            }
            this.isSettingDeliverableStatus = false;
        }
    }
});
</script>
